package fr.manyverse.app.presentation.components.soundboard

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.VolumeUp
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Pause
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Stop
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.layout
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Popup
import fr.manyverse.app.domain.model.Sound
import fr.manyverse.app.domain.model.SoundTag
import fr.manyverse.app.domain.model.SoundType
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.delete
import manyverse.shared.generated.resources.delete_sound
import manyverse.shared.generated.resources.delete_sound_confirm
import manyverse.shared.generated.resources.sound_volume
import manyverse.shared.generated.resources.tag_calm
import manyverse.shared.generated.resources.tag_combat
import manyverse.shared.generated.resources.tag_tension
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SoundListItem(
    sound: Sound,
    isPlaying: Boolean,
    isCrossfading: Boolean,
    volume: Float,
    onPlayClick: () -> Unit,
    onVolumeChange: (Float) -> Unit,
    onDeleteClick: (() -> Unit)? = null,
    isAmbientActive: Boolean = false,
    modifier: Modifier = Modifier
) {
    var showDeleteConfirmation by remember { mutableStateOf(false) }
    var showVolumeSlider by remember { mutableStateOf(false) }
    val isHighlighted = isPlaying || isAmbientActive
    val primaryColor = MaterialTheme.colorScheme.primary

    Card(
        modifier = modifier
            .fillMaxWidth()
            .clickable { onPlayClick() },
        colors = CardDefaults.cardColors(
            containerColor = if (isHighlighted) {
                MaterialTheme.colorScheme.surfaceContainerHigh
            } else {
                primaryColor.copy(alpha = 0.05f)
                    .compositeOver(MaterialTheme.colorScheme.surfaceContainerLow)
            }
        ),
        border = if (isHighlighted) {
            BorderStroke(1.dp, primaryColor)
        } else {
            BorderStroke(1.dp, primaryColor.copy(alpha = 0.18f))
        }
    ) {
        Column(
            modifier = Modifier.padding(start = 8.dp, end = 8.dp, top = 8.dp, bottom = 8.dp)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                // Play/Pause/Stop icon with circular background when active
                val showActiveButton = when {
                    sound.type == SoundType.AMBIENT -> isAmbientActive
                    else -> isPlaying
                }
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .background(
                            color = if (showActiveButton) MaterialTheme.colorScheme.primaryContainer else Color.Transparent,
                            shape = CircleShape
                        ),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        imageVector = when {
                            sound.type == SoundType.AMBIENT -> if (isAmbientActive) Icons.Default.Stop else Icons.Default.PlayArrow
                            isPlaying -> Icons.Default.Pause
                            else -> Icons.Default.PlayArrow
                        },
                        contentDescription = when {
                            sound.type == SoundType.AMBIENT -> if (isAmbientActive) "Stop" else "Play"
                            isPlaying -> "Pause"
                            else -> "Play"
                        },
                        tint = if (isHighlighted) primaryColor else MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.size(20.dp)
                    )
                }

                // Sound name
                Text(
                    text = sound.name,
                    style = MaterialTheme.typography.titleMedium,
                    color = if (isHighlighted) primaryColor else MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.weight(1f)
                )

                // Crossfading indicator
                if (isCrossfading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        strokeWidth = 2.dp
                    )
                }

                // Volume button + vertical slider popup
                val thumbColor = if (isHighlighted) primaryColor else MaterialTheme.colorScheme.outline
                Box {
                    val onThumbColor = if (isHighlighted) {
                        MaterialTheme.colorScheme.onPrimary
                    } else {
                        MaterialTheme.colorScheme.surface
                    }
                    Row(
                        modifier = Modifier
                            .background(
                                color = thumbColor,
                                shape = RoundedCornerShape(6.dp)
                            )
                            .clickable { showVolumeSlider = !showVolumeSlider }
                            .padding(horizontal = 8.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(4.dp)
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.VolumeUp,
                            contentDescription = stringResource(Res.string.sound_volume),
                            tint = onThumbColor,
                            modifier = Modifier.size(14.dp)
                        )
                        Text(
                            text = "${(volume * 100).roundToInt()}%",
                            style = MaterialTheme.typography.labelMedium,
                            color = onThumbColor
                        )
                    }
                    if (showVolumeSlider) {
                        Popup(
                            alignment = Alignment.BottomCenter,
                            onDismissRequest = { showVolumeSlider = false }
                        ) {
                            Card(
                                elevation = CardDefaults.cardElevation(defaultElevation = 8.dp),
                                shape = RoundedCornerShape(12.dp)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .padding(vertical = 6.dp)
                                        .width(36.dp)
                                        .height(120.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Slider(
                                        value = volume,
                                        onValueChange = onVolumeChange,
                                        onValueChangeFinished = { showVolumeSlider = false },
                                        modifier = Modifier
                                            .layout { measurable, constraints ->
                                                val placeable = measurable.measure(
                                                    Constraints(
                                                        minWidth = constraints.minHeight,
                                                        maxWidth = constraints.maxHeight,
                                                        minHeight = constraints.minWidth,
                                                        maxHeight = constraints.maxWidth,
                                                    )
                                                )
                                                layout(placeable.height, placeable.width) {
                                                    placeable.place(
                                                        x = -(placeable.width - placeable.height) / 2,
                                                        y = -(placeable.height - placeable.width) / 2
                                                    )
                                                }
                                            }
                                            .graphicsLayer { rotationZ = -90f },
                                        thumb = {
                                            Box(
                                                modifier = Modifier
                                                    .size(16.dp)
                                                    .background(thumbColor, CircleShape)
                                            )
                                        },
                                        track = { sliderState ->
                                            val inactive = thumbColor.copy(alpha = 0.3f)
                                            Canvas(modifier = Modifier.fillMaxWidth()) {
                                                val y = size.height / 2
                                                val stroke = 3.dp.toPx()
                                                val activeEnd = size.width * sliderState.value.coerceIn(0f, 1f)
                                                drawLine(inactive, Offset(0f, y), Offset(size.width, y), stroke, StrokeCap.Round)
                                                drawLine(thumbColor, Offset(0f, y), Offset(activeEnd, y), stroke, StrokeCap.Round)
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    }
                }

                // Delete button
                if (onDeleteClick != null) {
                    IconButton(
                        onClick = { showDeleteConfirmation = true },
                        modifier = Modifier.size(36.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Delete,
                            contentDescription = stringResource(Res.string.delete_sound),
                            tint = MaterialTheme.colorScheme.error
                        )
                    }
                }
            }

            // Tags (and duration for MUSIC), with waveform for playing music
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(start = 8.dp, top = 5.dp),
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (sound.type == SoundType.MUSIC) {
                    sound.durationSeconds?.let { seconds ->
                        val minutes = seconds / 60
                        val secs = seconds % 60
                        Text(
                            text = "$minutes:${secs.toString().padStart(2, '0')}",
                            style = MaterialTheme.typography.labelSmall,
                            color = if (isHighlighted) primaryColor else MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(end = 8.dp)
                        )
                    }
                }
                sound.tags.forEach { tag ->
                    val palette = soundTagPalette(tag)
                    Box(
                        modifier = Modifier
                            .background(palette.container, RoundedCornerShape(4.dp))
                            .padding(horizontal = 6.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = getTagLabel(tag),
                            style = MaterialTheme.typography.labelSmall,
                            color = palette.label
                        )
                    }
                }

                if (sound.type == SoundType.MUSIC && isPlaying) {
                    Spacer(Modifier.weight(1f))
                    AudioWaveform(
                        color = primaryColor,
                        modifier = Modifier.size(width = 64.dp, height = 16.dp)
                    )
                }
            }
        }
    }

    // Delete confirmation dialog
    if (showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { showDeleteConfirmation = false },
            title = { Text(stringResource(Res.string.delete_sound)) },
            text = { Text(stringResource(Res.string.delete_sound_confirm)) },
            confirmButton = {
                TextButton(
                    onClick = {
                        showDeleteConfirmation = false
                        onDeleteClick?.invoke()
                    }
                ) {
                    Text(
                        text = stringResource(Res.string.delete),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { showDeleteConfirmation = false }) {
                    Text(stringResource(Res.string.cancel))
                }
            }
        )
    }
}

@Composable
private fun AudioWaveform(
    color: Color,
    modifier: Modifier = Modifier
) {
    val t = rememberInfiniteTransition(label = "waveform")
    val b0 by t.animateFloat(0.25f, 1.00f, infiniteRepeatable(tween(370, easing = LinearEasing), RepeatMode.Reverse), "b0")
    val b1 by t.animateFloat(0.70f, 0.95f, infiniteRepeatable(tween(490, easing = LinearEasing), RepeatMode.Reverse), "b1")
    val b2 by t.animateFloat(0.20f, 0.85f, infiniteRepeatable(tween(310, easing = LinearEasing), RepeatMode.Reverse), "b2")
    val b3 by t.animateFloat(0.90f, 0.35f, infiniteRepeatable(tween(570, easing = LinearEasing), RepeatMode.Reverse), "b3")
    val b4 by t.animateFloat(0.50f, 1.00f, infiniteRepeatable(tween(430, easing = LinearEasing), RepeatMode.Reverse), "b4")
    val b5 by t.animateFloat(0.30f, 0.80f, infiniteRepeatable(tween(290, easing = LinearEasing), RepeatMode.Reverse), "b5")
    val b6 by t.animateFloat(0.75f, 0.25f, infiniteRepeatable(tween(520, easing = LinearEasing), RepeatMode.Reverse), "b6")
    val fractions = remember(b0, b1, b2, b3, b4, b5, b6) {
        floatArrayOf(b0, b1, b2, b3, b4, b5, b6)
    }
    Canvas(modifier = modifier) {
        val barCount = 7
        val barWidth = size.width / (barCount * 3 - 1)
        val step = barWidth * 3
        repeat(barCount) { i ->
            val barHeight = size.height * fractions[i]
            val x = i * step + barWidth / 2f
            drawLine(
                color = color,
                start = Offset(x, (size.height - barHeight) / 2f),
                end = Offset(x, (size.height + barHeight) / 2f),
                strokeWidth = barWidth,
                cap = StrokeCap.Round
            )
        }
    }
}

@Composable
private fun getTagLabel(tag: SoundTag): String {
    return when (tag) {
        SoundTag.CALM -> stringResource(Res.string.tag_calm)
        SoundTag.COMBAT -> stringResource(Res.string.tag_combat)
        SoundTag.TENSION -> stringResource(Res.string.tag_tension)
    }
}
